import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { formatAmount, formatDateOnly } from '../../../utils/global';
import { fetchInvoicePaymentHistory, formatMoneyInput, roundMoney } from './paymentProposalUtils';
import { warningLabel } from './paymentDuplicateWarning';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import { harmonateProceedButtonSx, harmonateCancelButtonSx } from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const PAYMENT_METHODS = ['ACH', 'WIRE', 'CHECK'];

const paymentStatusChipProps = (status) => {
  const normalized = (status || '').toUpperCase();
  if (normalized === 'PROPOSED') return { color: 'warning', variant: 'outlined' };
  if (normalized === 'PENDING') return { color: 'info', variant: 'outlined' };
  if (normalized === 'PAID' || normalized === 'COMPLETED') return { color: 'success', variant: 'filled' };
  if (normalized === 'CANCELLED' || normalized === 'SUPERSEDED') return { color: 'default', variant: 'outlined' };
  return { color: 'default', variant: 'outlined' };
};

const PaymentProposalDrawer = ({
  open,
  onClose,
  row,
  savedPayment,
  banks,
  getBankDisplayName,
  tenantId,
  amountFormat,
  dateFormat,
  initialForm,
  onSave,
  saving = false,
  duplicateWarnings = [],
  onOpenMatch,
  onDismissDuplicate,
  onVoidDuplicate,
  duplicateActionId = null,
}) => {
  const { canEdit } = useFunctionPermissions(FC.PAYMENT_INTELLIGENCE);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);

  useEffect(() => {
    if (open) {
      setForm(initialForm);
      setErrors({});
      setHistoryRefreshKey((key) => key + 1);
    }
  }, [open, initialForm]);

  useEffect(() => {
    if (!open || !row?.apInvoiceId || !tenantId) {
      setHistory([]);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setHistoryLoading(true);
        const enriched = await fetchInvoicePaymentHistory(tenantId, row.apInvoiceId);
        if (!cancelled) {
          setHistory(enriched.filter(
            ({ transaction }) => (transaction?.paymentStatus || '').toUpperCase() !== 'CANCELLED'
          ));
        }
      } catch {
        if (!cancelled) setHistory([]);
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, row?.apInvoiceId, tenantId, historyRefreshKey]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const isPartialPayment = form.paymentType === 'partial';
  const actualPaid = Number(row?.paidAmount) || 0;
  const balanceDue = roundMoney(row?.balance);
  const totalProposedPay = savedPayment?.saved ? roundMoney(savedPayment.totalPayAmount ?? savedPayment.payAmount) : 0;
  const remainingAvailable = savedPayment?.saved
    ? roundMoney(savedPayment.remainingBalance ?? Math.max(0, balanceDue - totalProposedPay))
    : balanceDue;
  const totalPaid = roundMoney(actualPaid);
  const invoiceAlreadyPaid = (row?.paymentStatus || '').toUpperCase() === 'PAID';
  const displayStatus = totalProposedPay > 0.001 && !invoiceAlreadyPaid
    ? 'Proposed'
    : balanceDue <= 0.01
      ? 'Paid'
      : actualPaid > 0
        ? 'Partial'
        : 'Unpaid';

  const resolveHistoryBankName = (mapping, transaction) => {
    const bankId = transaction?.bankId || mapping?.bankId;
    if (!bankId) return '—';
    const bank = banks.find((item) => item.bankId === bankId);
    return getBankDisplayName(bank) || '—';
  };

  const handlePaymentTypeChange = (paymentType) => {
    setForm((prev) => {
      if (paymentType === 'full') {
        return { ...prev, paymentType, payAmount: formatMoneyInput(remainingAvailable) };
      }
      const currentAmount = Number(prev.payAmount);
      const keepAmount = currentAmount > 0 && currentAmount < remainingAvailable - 0.001
        ? prev.payAmount
        : '';
      return { ...prev, paymentType, payAmount: keepAmount };
    });
    setErrors((prev) => ({ ...prev, payAmount: undefined }));
  };

  const handleSave = () => {
    if (!canEdit || !row) return;

    const nextErrors = {};
    const payAmount = form.paymentType === 'full'
      ? remainingAvailable
      : Number(form.payAmount);

    if (form.paymentType === 'partial') {
      if (!form.payAmount || Number.isNaN(payAmount) || payAmount <= 0) {
        nextErrors.payAmount = 'Enter a payment amount greater than zero';
      } else if (payAmount > remainingAvailable + 0.001) {
        nextErrors.payAmount = `Amount cannot exceed remaining balance (${formatAmount(remainingAvailable, amountFormat)})`;
      } else if (remainingAvailable > 0.001 && payAmount >= remainingAvailable - 0.001) {
        nextErrors.payAmount = 'Use Full Payment to pay the entire remaining balance';
      }
    } else if (remainingAvailable <= 0.001) {
      nextErrors.payAmount = 'No remaining balance available to pay';
    }

    if (banks.length && !form.bankId) {
      nextErrors.bankId = 'Select a bank';
    }

    if (!form.paymentMethod) {
      nextErrors.paymentMethod = 'Select a payment method';
    }

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    onSave({
      apInvoiceId: row.apInvoiceId,
      payAmount,
      bankId: form.bankId || null,
      paymentMethod: form.paymentMethod,
      isPartial: form.paymentType === 'partial',
    });
  };

  if (!row) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 700,
          maxWidth: '100%',
        },
      }}
    >
      <Box sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Payment Details</Typography>
            <Typography variant="body2" color="text.secondary">{row.invoiceNumber}</Typography>
            <Typography variant="caption" color="text.secondary" display="block">{row.vendorName}</Typography>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>

        {duplicateWarnings.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {duplicateWarnings.map((warning) => (
              <Box key={`${warning.paymentId}-${warning.matchedPaymentId}`} sx={{ mb: 1 }}>
                <Typography variant="body2">{warningLabel(warning)}</Typography>
                {warning.matchedInvoiceNumber && (
                  <Button
                    size="small"
                    onClick={() => onOpenMatch?.(warning.matchedApInvoiceId)}
                    sx={{ px: 0, textTransform: 'none' }}
                  >
                    View matching payment {warning.matchedInvoiceNumber}
                  </Button>
                )}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    disabled={!canEdit || duplicateActionId === warning.paymentId}
                    onClick={() => onDismissDuplicate?.(warning)}
                  >
                    Not a duplicate
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    disabled={!canEdit || duplicateActionId === warning.paymentId}
                    onClick={() => onVoidDuplicate?.(warning)}
                  >
                    Void as duplicate
                  </Button>
                </Box>
              </Box>
            ))}
          </Alert>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Invoice Amount</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {formatAmount(row.amount, amountFormat)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Paid</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {formatAmount(totalPaid, amountFormat)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Balance</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: ht.text }}>
              {formatAmount(balanceDue, amountFormat)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Status</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{displayStatus}</Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Payment Setup</Typography>

        <FormControl sx={{ mb: 2 }}>
          <RadioGroup
            row
            value={form.paymentType || 'full'}
            onChange={(e) => handlePaymentTypeChange(e.target.value)}
          >
            <FormControlLabel
              value="full"
              control={<Radio size="small" sx={{ color: ht.green, '&.Mui-checked': { color: ht.green } }} />}
              label="Full Payment"
            />
            <FormControlLabel
              value="partial"
              control={<Radio size="small" sx={{ color: ht.green, '&.Mui-checked': { color: ht.green } }} />}
              label="Partial Payment"
            />
          </RadioGroup>
        </FormControl>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
          <TextField
            label="Bank"
            select
            fullWidth
            size="small"
            value={form.bankId}
            onChange={(e) => updateField('bankId', e.target.value)}
            error={Boolean(errors.bankId)}
            helperText={errors.bankId || (banks.length ? '' : 'No active banks configured')}
            disabled={!banks.length}
          >
            <MenuItem value="">
              <em>Select bank</em>
            </MenuItem>
            {banks.map((bank) => (
              <MenuItem key={bank.bankId} value={bank.bankId}>
                {getBankDisplayName(bank)}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Payment Method"
            select
            fullWidth
            size="small"
            value={form.paymentMethod}
            onChange={(e) => updateField('paymentMethod', e.target.value)}
            error={Boolean(errors.paymentMethod)}
            helperText={errors.paymentMethod}
          >
            {PAYMENT_METHODS.map((method) => (
              <MenuItem key={method} value={method}>{method}</MenuItem>
            ))}
          </TextField>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
          <TextField
            label="Pay Amount"
            type="number"
            fullWidth
            size="small"
            value={form.payAmount}
            onChange={(e) => updateField('payAmount', e.target.value)}
            error={Boolean(errors.payAmount)}
            helperText={
              errors.payAmount
              || (isPartialPayment
                ? `Enter amount up to ${formatAmount(remainingAvailable, amountFormat)}`
                : `Pay remaining balance: ${formatAmount(remainingAvailable, amountFormat)}`)
            }
            inputProps={{ min: 0, max: remainingAvailable, step: 0.01 }}
            disabled={!isPartialPayment}
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 3 }}>
          <Button onClick={onClose} disabled={saving} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !canEdit}
            sx={harmonateProceedButtonSx}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Payment History</Typography>

        <Box sx={{ flex: 1, overflow: 'auto', minHeight: 120 }}>
          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : history.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No prior payments for this invoice.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">Remaining</TableCell>
                  <TableCell>Bank</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map(({ mapping, transaction }) => (
                  <TableRow key={mapping.mappingId || `${mapping.paymentId}-${mapping.mappingDate}`}>
                    <TableCell>
                      {mapping.mappingDate
                        ? formatDateOnly(mapping.mappingDate, dateFormat)
                        : '—'}
                    </TableCell>
                    <TableCell align="right">
                      {formatAmount(mapping.paidAmount, amountFormat)}
                    </TableCell>
                    <TableCell align="right">
                      {formatAmount(mapping.balanceAmount, amountFormat)}
                    </TableCell>
                    <TableCell>{resolveHistoryBankName(mapping, transaction)}</TableCell>
                    <TableCell>{transaction?.paymentMethod || '—'}</TableCell>
                    <TableCell>
                      {transaction?.paymentStatus ? (
                        <Chip
                          label={transaction.paymentStatus}
                          size="small"
                          color={paymentStatusChipProps(transaction.paymentStatus).color}
                          variant={paymentStatusChipProps(transaction.paymentStatus).variant}
                        />
                      ) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default PaymentProposalDrawer;
